import { http as mswHttp, HttpResponse } from 'msw';
import {
  ActionTypeCreate,
  ActionTypeDelete,
  ActionTypeUpdate,
  ActorTypeSystem,
  ActorTypeUser,
  ActorTypeWeave,
  AuditEvent,
  ChangeTypeBlockNumber,
  ChangeTypeSetAll,
  ChangeTypeSetDisabled,
  ChangeTypeSetInbound,
  ChangeTypeUnblockNumber,
  ChangeTypeUpdateHoldMusic,
  ObjectTypeCallBlocking,
  ObjectTypeCallRecording,
  ObjectTypeHoldMusic,
  SearchAuditEventsResponse,
} from './types';

const generatedActors = [
  { userId: 'user-1', userName: 'Jane Smith', email: 'jane@example.com', actorType: ActorTypeUser },
  { userId: 'user-2', userName: 'John Doe', email: 'john@example.com', actorType: ActorTypeUser },
  { userId: 'user-3', userName: 'Alice Johnson', email: 'alice@example.com', actorType: ActorTypeUser },
  { userId: 'emp-1', userName: 'Support Agent', email: 'support@weave.com', actorType: ActorTypeWeave },
  { userId: 'sys-1', userName: 'System', actorType: ActorTypeSystem },
];

const generatedMockAuditEvents: AuditEvent[] = Array.from({ length: 54 }, (_, index) => {
  const eventNumber = index + 7;
  const day = String((index % 28) + 1).padStart(2, '0');
  const hour = String((index % 12) + 8).padStart(2, '0');
  const minute = String((index * 7) % 60).padStart(2, '0');
  const actor = generatedActors[index % generatedActors.length];

  if (index % 3 === 0) {
    const isCreate = index % 2 === 0;
    return {
      id: `evt-${String(eventNumber).padStart(3, '0')}`,
      tenantId: 'tenant-abc',
      actor,
      action: isCreate ? ActionTypeCreate : ActionTypeDelete,
      objectType: ObjectTypeCallBlocking,
      objectName: 'Call Blocking List',
      objectId: 'tenant-abc',
      createTime: `2026-05-${day}T${hour}:${minute}:00Z`,
      changes: [
        {
          type: isCreate ? ChangeTypeBlockNumber : ChangeTypeUnblockNumber,
          blockNumber: { phoneNumber: `+1801555${String(1000 + index).slice(-4)}` },
        },
      ],
    };
  }

  if (index % 3 === 1) {
    const recordingChange =
      index % 4 === 0 ? ChangeTypeSetAll : index % 4 === 1 ? ChangeTypeSetInbound : ChangeTypeSetDisabled;
    return {
      id: `evt-${String(eventNumber).padStart(3, '0')}`,
      tenantId: 'tenant-abc',
      actor,
      action: ActionTypeUpdate,
      objectType: ObjectTypeCallRecording,
      objectName: 'Call Recording Setting',
      objectId: 'tenant-abc',
      createTime: `2026-05-${day}T${hour}:${minute}:00Z`,
      changes: [{ type: recordingChange, callRecording: {} }],
    };
  }

  return {
    id: `evt-${String(eventNumber).padStart(3, '0')}`,
    tenantId: 'tenant-abc',
    actor,
    action: ActionTypeUpdate,
    objectType: ObjectTypeHoldMusic,
    objectName: 'Hold Music',
    objectId: 'tenant-abc',
    createTime: `2026-05-${day}T${hour}:${minute}:00Z`,
    changes: [
      {
        type: ChangeTypeUpdateHoldMusic,
        updateHoldMusic: {
          locations: [
            {
              mediaItemId: `media-${String(100 + index)}`,
              mediaItemName: `hold-track-${String(index + 1).padStart(2, '0')}.mp3`,
              locationId: `loc-${(index % 8) + 1}`,
              locationName: `Location ${(index % 8) + 1}`,
            },
          ],
        },
      },
    ],
  };
});

export const mockAuditEvents: AuditEvent[] = [
  {
    id: 'evt-001',
    tenantId: 'tenant-abc',
    actor: { userId: 'user-1', userName: 'Jane Smith', email: 'jane@example.com', actorType: ActorTypeUser },
    action: ActionTypeCreate,
    objectType: ObjectTypeCallBlocking,
    objectName: 'Call Blocking List',
    objectId: 'tenant-abc',
    createTime: '2026-06-15T10:30:00Z',
    changes: [{ type: ChangeTypeBlockNumber, blockNumber: { phoneNumber: '+18015551234' } }],
  },
  {
    id: 'evt-002',
    tenantId: 'tenant-abc',
    actor: { userId: 'user-2', userName: 'John Doe', email: 'john@example.com', actorType: ActorTypeUser },
    action: ActionTypeDelete,
    objectType: ObjectTypeCallBlocking,
    objectName: 'Call Blocking List',
    objectId: 'tenant-abc',
    createTime: '2026-06-14T14:22:00Z',
    changes: [{ type: ChangeTypeUnblockNumber, blockNumber: { phoneNumber: '+18015559876' } }],
  },
  {
    id: 'evt-003',
    tenantId: 'tenant-abc',
    actor: {
      userId: 'user-1',
      userName: 'Janeth Smithsonion The Third',
      email: 'jane+a.really.long.email@example.com',
      actorType: ActorTypeUser,
    },
    action: ActionTypeUpdate,
    objectType: ObjectTypeCallRecording,
    objectName: 'Call Recording Setting',
    objectId: 'tenant-abc',
    createTime: '2026-06-13T09:15:00Z',
    changes: [{ type: ChangeTypeSetInbound, callRecording: {} }],
  },
  {
    id: 'evt-004',
    tenantId: 'tenant-abc',
    actor: { userId: 'sys-1', userName: 'System', actorType: ActorTypeSystem },
    action: ActionTypeUpdate,
    objectType: ObjectTypeCallRecording,
    objectName: 'Call Recording Setting',
    objectId: 'tenant-abc',
    createTime: '2026-06-12T16:45:00Z',
    changes: [{ type: ChangeTypeSetAll, callRecording: {} }],
  },
  {
    id: 'evt-005',
    tenantId: 'tenant-abc',
    actor: { userId: 'user-3', userName: 'Alice Johnson', email: 'alice@example.com', actorType: ActorTypeUser },
    action: ActionTypeUpdate,
    objectType: ObjectTypeHoldMusic,
    objectName: 'Hold Music',
    objectId: 'tenant-abc',
    createTime: '2026-06-11T11:00:00Z',
    changes: [
      {
        type: ChangeTypeUpdateHoldMusic,
        updateHoldMusic: {
          locations: [
            {
              mediaItemId: 'media-001',
              mediaItemName: 'jazz-hold.mp3',
              locationId: 'loc-1',
              locationName: 'Main Office',
            },
            {
              mediaItemId: 'media-002',
              mediaItemName: 'classical-hold.mp3',
              locationId: 'loc-2',
              locationName: 'Branch Office',
            },
          ],
        },
      },
    ],
  },
  {
    id: 'evt-006',
    tenantId: 'tenant-abc',
    actor: { userId: 'emp-1', userName: 'Support Agent', email: 'support@weave.com', actorType: ActorTypeWeave },
    action: ActionTypeUpdate,
    objectType: ObjectTypeCallRecording,
    objectName: 'Call Recording Setting',
    objectId: 'tenant-abc',
    createTime: '2026-06-10T08:30:00Z',
    changes: [{ type: ChangeTypeSetDisabled, callRecording: {} }],
  },
  {
    id: 'evt-006-2',
    tenantId: 'tenant-abc',
    actor: { userId: 'emp-1', userName: 'Support Agent', email: 'support@weave.com', actorType: ActorTypeWeave },
    action: ActionTypeUpdate,
    objectType: ObjectTypeCallRecording,
    objectName: 'Call Recording Setting',
    objectId: 'tenant-abc',
    createTime: '2026-06-10T08:30:00Z',
    changes: [
      { type: ChangeTypeSetDisabled, callRecording: {} },
      { type: ChangeTypeSetInbound, callRecording: {} },
    ],
  },
  ...generatedMockAuditEvents,
];

export const mockSearchAuditEventsResponse: SearchAuditEventsResponse = {
  auditEvents: mockAuditEvents,
  nextPageToken: undefined,
  trackingStartTime: '2026-05-01T11:23:00Z',
};

export const searchAuditEventsMswHandler = mswHttp.post('/phone/audits/v1/audit-events:searchAuditEvents', () => {
  return HttpResponse.json(mockSearchAuditEventsResponse);
});
